import { createSelector, createSlice, isAnyOf } from "@reduxjs/toolkit"
import { withRoot } from "@nova/with-root"
import {
    AnyAction,
    IconPackOption,
    IconShape,
    LineStroke,
    LineWidth,
    SidePanelLayerType,
    ToolAttributes,
    ToolName,
    AreaUnit,
    DistanceUnit,
} from "@nova/constants"
import { setBoundingBoxId } from "features/map/layers/slice"
import { setSelectedTool } from "./actions"
import { deleteIcon, updateIcon } from "features/pubsub/entityActions/cubes/icon"
import { deleteText } from "features/pubsub/entityActions/cubes/text"
import { deleteLine } from "features/pubsub/entityActions/cubes/line"
import { setSnapshotOpen } from "plugins/PDFv2/PDFSlice"
import { defaultIconColor } from "features/map/layers/icons/constants"
import { encodeColorToNumber } from "@nova/serializer/src/client/util"
import { RootState } from "integrations/redux"
import { selectSelectedLayers } from "panels/content/side-panel/slice.new"
import { defaultLineColor, defaultPolygonFill, defaultPolygonOutline } from "./Line/constants"
import { setPictureInPicture } from "features/map/control/slice"
import { ReactElement } from "react"
import { clearEditingData } from "plugins/DrawPerimeter/FirePerimeterSlice"
import { clearEditingData as clearLineEditingData, moveLineToEditable } from "./Line/LineSlice"
import { clearEditingData as clearRulerEditingData, moveRulerToEditable } from "./Ruler/slice"
import { loadProject } from "features/map/control/thunks"
import { cancelMaskDrawing } from "plugins/Orthomosaic/OrthoMaskSlice"

const uiStateName = "toolbox-registry-UI"
const root = (state: RootState) => state[uiStateName]

type IconTemplate = {
    pack: IconPackOption
    color: number
    url: string
    name: string
    width: number
    outline: number
    size: number
}

type LineTemplate = {
    color: number
    outline: number
    fill: number
    stroke: LineStroke
    width: LineWidth
}

type RulerTemplate = {
    distanceUnit?: DistanceUnit
    areaUnit?: AreaUnit
}

type TextTemplate = {
    // Optional because the first text placement's color will be determined by the selected map base layer.
    color?: number
    isBold: boolean
    // Optional because the first text placement's fontSize will be determined by the zoom level.
    // This value will only be reused if the zoom level doesn't change
    fontSize?: {
        value: number
        zoomLevel: number
    }
    // Optional because the outline color will be determine by the inverse of the text's default color
    outlineColor?: number
    outlineWidth: LineWidth
}

type UIState = {
    activePack: string
    active?: ToolName
    selected?: { toolName: ToolName; tool: ToolAttributes }
    timestamp?: string
    numberStartAt: number
    template: {
        icon: IconTemplate
        line: LineTemplate
        ruler: RulerTemplate
        text: TextTemplate
    }
    appearence: Omit<ToolAttributes, "id" | "coordinates"> | undefined
    iconIsBeingDragged: boolean
}
const {
    reducer: uiStateReducer,
    actions: {
        setActiveTool,
        updateIconTemplate,
        updateLineTemplate,
        updateRulerTemplate,
        updateTextTemplate,
        setIconIsBeingDragged,
        incrementNumberStartAt,
        setIconNumberStartAt,
    },
} = createSlice({
    name: uiStateName,
    initialState: {
        activePack: "shape",
        active: undefined as ToolName | undefined,
        selected: undefined as { toolName: ToolName; tool: ToolAttributes } | undefined,
        appearence: undefined as Omit<ToolAttributes, "id" | "coordinates"> | undefined,
        timestamp: undefined as string | undefined,
        numberStartAt: 1,
        template: {
            icon: {
                pack: IconPackOption.Shape,
                color: encodeColorToNumber(defaultIconColor),
                url: IconShape.Circle,
                name: "Point",
                width: LineWidth.normal,
                outline: 0xffffffff,
            },
            line: {
                color: encodeColorToNumber(defaultLineColor),
                outline: encodeColorToNumber(defaultPolygonOutline),
                fill: encodeColorToNumber(defaultPolygonFill),
                stroke: LineStroke.normal,
                width: LineWidth.normal,
            },
            ruler: {} as RulerTemplate,
            text: {
                isBold: false,
                outlineWidth: LineWidth.normal,
            },
        },
        iconIsBeingDragged: false,
    } as UIState,
    reducers: {
        setActiveTool: (state, action: { payload: ToolName | undefined }) => {
            state.active = action.payload
            state.timestamp = new Date().toISOString()
        },
        setIconNumberStartAt: (state, { payload: num }: { payload: number }) => {
            if (num.toString() !== num.toFixed(0) || num < 1 || num > 99) return
            state.numberStartAt = num
            state.template.icon.url = `numbers/${state.numberStartAt}`
            state.template.icon.name = state.numberStartAt.toString()
        },
        incrementNumberStartAt: (state) => {
            if (state.numberStartAt >= 99) state.numberStartAt = 1
            else state.numberStartAt++
            state.template.icon.url = `numbers/${state.numberStartAt}`
            state.template.icon.name = state.numberStartAt.toString()
        },
        updateIconTemplate: (state, action: { payload: Partial<IconTemplate> }) => {
            const { color, url, pack, name, outline, width } = action.payload
            if (color !== undefined) state.template.icon.color = color
            if (url !== undefined) state.template.icon.url = url
            if (pack !== undefined) state.template.icon.pack = pack
            if (name !== undefined) state.template.icon.name = name
            if (width !== undefined) state.template.icon.width = width
            if (outline !== undefined) state.template.icon.outline = outline
        },
        updateLineTemplate: (state, action: { payload: Partial<LineTemplate> }) => {
            const { color, outline, fill, stroke, width } = action.payload
            if (color !== undefined) state.template.line.color = color
            if (outline !== undefined) state.template.line.outline = outline
            if (fill !== undefined) state.template.line.fill = fill
            if (stroke !== undefined) state.template.line.stroke = stroke
            if (width !== undefined) state.template.line.width = width
        },
        updateRulerTemplate: (state, action: { payload: Partial<RulerTemplate> }) => {
            const { distanceUnit, areaUnit } = action.payload
            if (distanceUnit !== undefined) state.template.ruler.distanceUnit = distanceUnit
            if (areaUnit !== undefined) state.template.ruler.areaUnit = areaUnit
        },
        updateTextTemplate: (state, action: { payload: Partial<TextTemplate> }) => {
            const { color, isBold, fontSize, outlineColor, outlineWidth } = action.payload
            if (color !== undefined) state.template.text.color = color
            if (isBold !== undefined) state.template.text.isBold = isBold
            if (fontSize !== undefined) state.template.text.fontSize = fontSize
            if (outlineWidth !== undefined) state.template.text.outlineWidth = outlineWidth
            if (outlineColor !== undefined) state.template.text.outlineColor = outlineColor
        },
        setIconIsBeingDragged: (state, action: { payload: boolean }) => {
            state.iconIsBeingDragged = action.payload
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(updateIcon, (state, action) => {
                const currentIcon = state.selected?.tool
                if (!currentIcon) return
                state.selected = {
                    toolName: ToolName.Icon,
                    tool: { ...currentIcon, ...action.payload.updates },
                }
            })
            .addCase(setSnapshotOpen, (state, action) => {
                // Exit the active tool if the PDF snapshot opened.
                if (action.payload) {
                    state.active = undefined
                }
            })
            .addCase(setBoundingBoxId, (state) => {
                state.selected = undefined
            })
            .addCase(setSelectedTool, (state, action) => {
                state.selected = action.payload
            })
            .addCase(setPictureInPicture, (state) => {
                state.active = undefined
            })
            .addCase(clearEditingData, (state) => {
                if (state.active === ToolName.FirePerimeter) state.active = undefined
            })
            .addCase(clearLineEditingData, (state) => {
                if (state.active === ToolName.Line) state.active = undefined
            })
            .addCase(clearRulerEditingData, (state) => {
                if (state.active === ToolName.Ruler) state.active = undefined
            })
            .addCase(cancelMaskDrawing, (state) => {
                if (state.active === ToolName.OrthoMask) state.active = undefined
            })
            .addCase(moveLineToEditable, (state, action) => {
                const { color, outline, fill, stroke, width } = action.payload
                if (color !== undefined) state.template.line.color = color
                if (outline !== undefined) state.template.line.outline = outline
                if (fill !== undefined) state.template.line.fill = fill
                if (stroke !== undefined) state.template.line.stroke = stroke
                if (width !== undefined) state.template.line.width = width
            })
            .addCase(moveRulerToEditable, (state, action) => {
                const { distanceUnit, areaUnit } = action.payload
                if (distanceUnit !== undefined)
                    state.template.ruler.distanceUnit = distanceUnit as DistanceUnit
                if (areaUnit !== undefined) state.template.ruler.areaUnit = areaUnit as AreaUnit
            })
            .addCase(loadProject.fulfilled, (state, action) => {
                state.numberStartAt =
                    (action.payload.icons.reduce(
                        (acc, cur) => acc + +cur.url.startsWith("numbers"),
                        0
                    ) %
                        99) +
                    1
            })
            // If the selected map entity is deleted, ensure that it gets cleaned up here.
            .addMatcher(
                isAnyOf(deleteText, deleteLine, deleteIcon),
                (state, { payload: { id } }) => {
                    if (state.selected && state.selected.tool.id === id) {
                        state.selected = undefined
                    }
                }
            )
    },
})

const {
    selectActiveToolName,
    selectSelectedToolEntity,
    selectToolAppearence,
    selectIconNumberStartAt,
} = withRoot((r: AnyAction) => r[uiStateName] as UIState, {
    selectActiveToolName: () => (s) => s.active as ToolName | undefined,
    selectSelectedToolEntity: () => (s) => (s.selected ? s.selected.tool : undefined),
    selectToolAppearence: () => (s) => s.appearence,
    selectIconNumberStartAt: () => (s) => s.numberStartAt,
})

export const selectSelectedTool = createSelector([selectSelectedLayers], (selectedLayers) => {
    const firstLayer = selectedLayers[0]
    if (!firstLayer) return undefined

    let toolName: ToolName | undefined

    switch (firstLayer?.type) {
        case SidePanelLayerType.Text:
            toolName = ToolName.Text
            break
        case SidePanelLayerType.Line:
            toolName = ToolName.Line
            break
        case SidePanelLayerType.Ruler:
            toolName = ToolName.Ruler
            break
        case SidePanelLayerType.Icon:
            toolName = ToolName.Icon
            break
    }

    if (toolName) {
        return {
            toolName,
            tool: {
                ...firstLayer,
                ModalComponent: () => null,
            } as ToolAttributes & {
                ModalComponent?: ({ fireId }: { fireId: string }) => ReactElement | null
            },
        }
    }

    return undefined
})

export const selectIconTemplate = (state: RootState) => root(state).template.icon

export const selectLineTemplate = (state: RootState) => root(state).template.line

export const selectRulerTemplate = (state: RootState) => root(state).template.ruler

export const selectTextTemplate = (state: RootState) => root(state).template.text

export const selectIconIsBeingDragged = (state: RootState) => root(state).iconIsBeingDragged

// actions
export {
    setActiveTool,
    updateIconTemplate,
    updateLineTemplate,
    updateRulerTemplate,
    updateTextTemplate,
    setIconIsBeingDragged,
    incrementNumberStartAt,
    setIconNumberStartAt,
}

// selectors
export {
    selectActiveToolName,
    selectSelectedToolEntity,
    selectToolAppearence,
    selectIconNumberStartAt,
}

// reducer
export const reducer = {
    [uiStateName]: uiStateReducer,
}
